/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality. 
 */
import { CustomOptOut } from './customOptOut';
import { modal } from 'commonAssets/lib/modal.js';
import { toggleFieldError } from 'commonAssets/lib/toggleFieldError.js';
import { CustomErrorHandler } from "./customErrorHandler"

/**
 * Initialize error handler
 */
export const errorHandler = new CustomErrorHandler()

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log('[Initiate claim]...');
    const isGenericClaim = claim.data.zero_zero_id == null;
    toggleGoogleCustomerIdField(isGenericClaim);
    document.getElementById('claimFormDeadline')?.remove();
    document.getElementById('claim-fields')?.classList.add('d-none');
    document.getElementById('payment-widget')?.classList.add('d-none');
    ['first_name', 'last_name'].forEach(name => {
        const field = document.querySelector(`input[name="${name}"]`);
        if (field) field.disabled = Boolean(field.value);
    });
    return claim;
}

// No-op DST bypass: deficiency portal preserves the claimant's existing payment_method/payment_token.
export function customDstProcess(claim) {
    console.log('[Custom DST Process]...');
    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log('[Load class member]...');
    if (claim.data.google_customer_id != null) {
        document.querySelector('input[name="google_customer_id"]').value = claim.data.google_customer_id;
    }
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Promise<Object>} The processed claim.
 */
export async function customProcessClaim(claim) {
    console.log('[Process claim]...');
    claim.data.google_customer_id = claim.data.google_customer_id ?? document.querySelector('input[name="google_customer_id"]').value.trim();

    const fileInput = document.getElementById('file-upload');
    const documentationSection = document.getElementById('documentation');

    const newUploadsCount = Array.isArray(claim?.uploads) ? claim.uploads.length : 0;
    const existingFiles = Array.isArray(claim?.class_member?.claim_files) ? claim.class_member.claim_files : [];
    const existingSuccessfulCount = existingFiles.filter(f => f && f.failed !== true).length;

    if (newUploadsCount === 0 && existingSuccessfulCount === 0) {
        claim.flags.valid = false;
        if (fileInput && documentationSection) {
            toggleFieldError(fileInput, true, 'Required');
            documentationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            fileInput.focus();
        }
        return claim;
    }

    if (fileInput) toggleFieldError(fileInput, false);
    return claim;
}

function toggleGoogleCustomerIdField(isVisible = false) {
    const classMemberIdentifier = document.querySelectorAll('.class-member-identifier');
    classMemberIdentifier.forEach(element => {
        element.classList.toggle('d-none', !isVisible);
    });

    const customerId = document.querySelector('input[name="google_customer_id"]');
    if (customerId) {
        customerId.required = isVisible;
    }
}

/**
 * Handles the opt-out form added event.
 *
 * @param {Object} params - The parameters.
 * @param {CustomOptOut} params.ctx - The context object.
 * @param {Node} params.form - The form.
 */
export function handleOptOutFormAdded({ ctx, form }) {
    console.log('Opt-Out Form Added', ctx, form);

    if (ctx.exclusion.data.zero_zero_id == null) {
        return;
    }

    // populate google customer id field
    const googleCustomerId = form.querySelector('input[name="google_customer_id"]');
    if (googleCustomerId) {
        googleCustomerId.value = ctx.exclusion.data.google_customer_id;
    }

    // hide claim fields for known class members
    const claimFields = form.querySelector('#claim-fields');
    if (claimFields) {
        claimFields.classList.add('d-none');
    }
}

/**
 * Handles the opt-out form submit event.
 *
 * @param {Object} params - The parameters.
 * @param {Event} params.e - The event.
 * @param {CustomOptOut} params.ctx - The context object.
 */
export async function handleOptOutFormSubmit({ e, ctx }) {
    console.log('Opt-Out Form Submitted');
    const form = e.target;
    const locationTargetingClass = form.querySelector('input[name="location_targeting_class"]');
    const searchBundledClicksClass = form.querySelector('input[name="search_bundled_clicks_class"]');

    // for generic claims, user must select one of the classes
    if (ctx.exclusion.data.zero_zero_id == null && !locationTargetingClass.checked && !searchBundledClicksClass.checked) {
        modal('Invalid Selection', 'You must select at least one of the classes to opt out of.');
        return false;
    }

    // set google customer id
    ctx.exclusion.data.google_customer_id = ctx.exclusion.data.google_customer_id ?? form.elements.namedItem('google_customer_id').value.trim();
    return true;
}

/**
 * Handles the opt-out form removed event.
 *
 * @param {Object} params - The parameters.
 * @param {CustomOptOut} params.ctx - The context object.
 * @param {Node} params.form - The form.
 */
export function handleOptOutFormRemoved({ ctx, form }) {
    console.log('Opt-Out Form Removed', ctx, form);
}

/**
 * Handles lookup success.
 *
 * @param {Object} result - The lookup result data.
 * @returns {void}
 */
export function onLookupSuccess(result) {
    console.log('[Lookup success]...', result);
    modal('Claim Details Found', `
        <p>Below is the Customer ID associated with your Claim and the award amount. If you have any questions about the award amount, please contact us at <a href="mailto:info@adwordsclicksclassaction.com">info@adwordsclicksclassaction.com</a>.</p>
        <p><strong>Google Customer ID:</strong> ${result.google_customer_id || 'N/A'}</br><strong>Award Amount:</strong> ${formatCurrency(result.dist_amt) || 'N/A'}</p>
    `);
}

/**
 * Handles lookup error.
 *
 * @param {string} error - The error message.
 * @returns {void}
 */
export function onLookupError(error) {
    console.log('[Lookup error]...', error);
    modal('Notice ID Not Found', `
        <p>We could not find your claim details based on the provided Notice ID.</p>
        <p>Please verify your Notice ID and try again, or contact 
        <a href="mailto:info@adwordsclicksclassaction.com">info@adwordsclicksclassaction.com</a></p>
    `);
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}